import { axios } from '@getgreenline/homi-shared'
import Endpoints from '../../../constants/Endpoints'

const BASE_PATH = `${Endpoints.PLATFORM_GATEWAY_URL}/menu-service/api/v1`

interface IJsonApiResource<TAttributes> {
  id: string
  type: string
  attributes: TAttributes
}

interface IJsonApiListResponse<TAttributes> {
  data: Array<IJsonApiResource<TAttributes>>
  meta?: {
    nextPageCursor?: string
    prevPageCursor?: string
  }
}

interface IJsonApiSingleResponse<TAttributes> {
  data: IJsonApiResource<TAttributes>
}

interface IChannelAttributes {
  name: string
  environment: 'US' | 'GL'
  active: boolean
  createdAt?: string
  updatedAt?: string
}

export interface ISalesChannel {
  id: string
  name: string
  environment: 'US' | 'GL'
  active: boolean
  createdAt?: string
  updatedAt?: string
}

/** The menu service rejects anything larger; see `Page.size` on its common contract. */
const MAX_PAGE_SIZE = 50

/** Guards against a cursor that never terminates; 50 pages is far beyond any real channel count. */
const MAX_PAGES = 50

/**
 * Returns every sales channel, following the cursor rather than asking for one oversized page.
 * The service caps `page[size]` at 50 and answers 400 above it, so a single call both truncates
 * and, if the cap is exceeded, fails outright.
 */
export const getSalesChannels = async (): Promise<ISalesChannel[]> => {
  const channels: ISalesChannel[] = []
  let cursor: string | undefined
  let page = 0

  do {
    const res = await axios.get<IJsonApiListResponse<IChannelAttributes>>(
      `${BASE_PATH}/admin/channels`,
      {
        params: {
          'page[size]': MAX_PAGE_SIZE,
          ...(cursor && { 'page[after]': cursor }),
        },
      },
    )

    channels.push(
      ...(res.data.data ?? []).map((channel) => ({
        id: channel.id,
        name: channel.attributes.name,
        environment: channel.attributes.environment,
        active: channel.attributes.active,
        createdAt: channel.attributes.createdAt,
        updatedAt: channel.attributes.updatedAt,
      })),
    )

    cursor = res.data.meta?.nextPageCursor
    page += 1
  } while (cursor && page < MAX_PAGES)

  return channels
}

export const createSalesChannel = async (
  name: string,
  environment: 'US' | 'GL',
  active: boolean,
): Promise<ISalesChannel> => {
  const res = await axios.post<IJsonApiSingleResponse<IChannelAttributes>>(
    `${BASE_PATH}/admin/channels`,
    {
      data: {
        type: 'channels',
        attributes: {
          name,
          environment,
          active,
        },
      },
    },
  )

  return {
    id: res.data.data.id,
    name: res.data.data.attributes.name,
    environment: res.data.data.attributes.environment,
    active: res.data.data.attributes.active,
    createdAt: res.data.data.attributes.createdAt,
    updatedAt: res.data.data.attributes.updatedAt,
  }
}

export const updateSalesChannel = async (
  channelId: string,
  name: string,
  environment: 'US' | 'GL',
  active: boolean,
): Promise<ISalesChannel> => {
  const res = await axios.put<IJsonApiSingleResponse<IChannelAttributes>>(
    `${BASE_PATH}/admin/channels/${channelId}`,
    {
      data: {
        type: 'channels',
        attributes: {
          name,
          environment,
          active,
        },
      },
    },
  )

  return {
    id: res.data.data.id,
    name: res.data.data.attributes.name,
    environment: res.data.data.attributes.environment,
    active: res.data.data.attributes.active,
    createdAt: res.data.data.attributes.createdAt,
    updatedAt: res.data.data.attributes.updatedAt,
  }
}
